import React, { useState } from 'react';
import { useStore } from '../context/StoreContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import TenantProfileModal from '../components/TenantProfileModal';

const { FiPlus, FiSearch, FiUser, FiStar, FiChevronRight, FiFilter, FiTrendingUp, FiX } = FiIcons;

// ─── Add/Edit Tenant Modal ──────────────────────────────────────────
const TenantFormModal = ({ isOpen, onClose, onSave, tenant, customFieldDefs }) => {
  const tenantCustomFields = (customFieldDefs || []).filter(f => f.appliesTo === 'tenant' || f.appliesTo === 'both');
  const [form, setForm] = useState(tenant || {
    name: '', email: '', phone: '', employer: '', position: '',
    rating: 5, notes: '', tags: [], emergencyContact: '', customFields: {}
  });
  const [tagInput, setTagInput] = useState('');

  if (!isOpen) return null;

  const customFieldValues = form.customFields || {};
  const setCustomField = (fieldName, value) => {
    setForm(prev => ({ ...prev, customFields: { ...prev.customFields, [fieldName]: value } }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
    onClose();
  };

  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm({ ...form, tags: [...form.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setForm({ ...form, tags: form.tags.filter(t => t !== tag) });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">{tenant ? 'Edit Guest' : 'Add New Guest'}</h3>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <SafeIcon icon={FiX} size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Full Name *</label>
              <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" placeholder="Jane Smith" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Email *</label>
              <input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" placeholder="jane@example.com" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Phone</label>
              <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" placeholder="+1 (555) 123-4567" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Employer</label>
              <input value={form.employer} onChange={e => setForm({ ...form, employer: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" placeholder="Company name" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Position</label>
              <input value={form.position} onChange={e => setForm({ ...form, position: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" placeholder="Job title" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Rating (1-5)</label>
              <select value={form.rating} onChange={e => setForm({ ...form, rating: Number(e.target.value) })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none">
                {[5,4,3,2,1].map(n => <option key={n} value={n}>{n} Star{n > 1 ? 's' : ''}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Emergency Contact</label>
              <input value={form.emergencyContact} onChange={e => setForm({ ...form, emergencyContact: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" placeholder="Name: Phone" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Tags</label>
              <div className="flex gap-2 mb-2 flex-wrap">
                {(form.tags || []).map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded-lg">
                    {tag}
                    <button type="button" onClick={() => removeTag(tag)} className="text-indigo-400 hover:text-indigo-700"><SafeIcon icon={FiX} size={12} /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" placeholder="Corporate, Academic, etc." />
                <button type="button" onClick={addTag} className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200">Add</button>
              </div>
            </div>
            {tenantCustomFields.length > 0 && (
              <>
                <div className="col-span-2 pt-2 border-t border-gray-100">
                  <p className="text-xs font-semibold text-indigo-600 uppercase">Custom Fields</p>
                </div>
                {tenantCustomFields.map(cf => (
                  <div key={cf.name}>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">{cf.name}</label>
                    <input value={customFieldValues[cf.name] || ''} onChange={e => setCustomField(cf.name, e.target.value)}
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" placeholder={cf.name} />
                  </div>
                ))}
              </>
            )}
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Notes</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none resize-none" placeholder="Any notes about this guest..." />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
            <button type="submit" className="flex-1 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors">{tenant ? 'Save Changes' : 'Add Guest'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────
const Tenants = () => {
  const { data, addRecord, updateRecord } = useStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);

  const handleSaveTenant = (tenantData) => {
    if (editingTenant) {
      updateRecord('tenants', editingTenant.id, tenantData);
    } else {
      addRecord('tenants', tenantData);
    }
    setEditingTenant(null);
  };

  const filteredTenants = (data.tenants || []).filter(t => 
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.employer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-10">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 tracking-tight">Tenant Relationship Manager</h2>
          <p className="text-sm text-gray-500">Cultivate loyalty and manage detailed profiles for your portfolio.</p>
        </div>
        <button
          onClick={() => { setEditingTenant(null); setIsFormOpen(true); }}
          className="flex items-center px-5 py-3 bg-indigo-600 text-white rounded-2xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 font-black text-sm uppercase tracking-wider"
        >
          <SafeIcon icon={FiPlus} className="mr-2" /> Add New Guest
        </button>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm flex items-center justify-between group">
          <div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Tenant Base</p>
            <p className="text-3xl font-black text-gray-900 mt-1">{data.tenants.length}</p>
          </div>
          <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
            <SafeIcon icon={FiUser} size={24} />
          </div>
        </div>
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm flex items-center justify-between group">
          <div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Avg. Guest Rating</p>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-3xl font-black text-gray-900">4.8</p>
              <div className="flex text-yellow-400">
                <SafeIcon icon={FiStar} size={16} className="fill-current" />
              </div>
            </div>
          </div>
          <div className="w-12 h-12 bg-yellow-50 text-yellow-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
            <SafeIcon icon={FiStar} size={24} />
          </div>
        </div>
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm flex items-center justify-between group">
          <div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Repeat Rate</p>
            <p className="text-3xl font-black text-gray-900 mt-1">12%</p>
          </div>
          <div className="w-12 h-12 bg-green-50 text-green-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
            <SafeIcon icon={FiTrendingUp} size={24} />
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col md:flex-row gap-4 bg-white p-4 rounded-3xl shadow-sm border border-gray-100">
        <div className="relative flex-1">
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input 
            type="text" 
            placeholder="Search by name, email, or employer..."
            className="w-full pl-12 pr-4 py-3 bg-gray-50 border-none rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="flex items-center px-6 py-3 bg-gray-50 text-gray-500 rounded-2xl hover:bg-gray-100 transition font-bold text-sm">
          <SafeIcon icon={FiFilter} className="mr-2" /> Filters
        </button>
      </div>

      {/* Tenant Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTenants.map(tenant => {
          const prop = data.properties.find(p => p.tenantId === tenant.id);
          return (
            <div 
              key={tenant.id}
              onClick={() => setSelectedTenant(tenant)}
              className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-indigo-100 transition-all cursor-pointer group relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <SafeIcon icon={FiChevronRight} className="text-indigo-400" size={20} />
              </div>
              
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center text-xl font-black group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                  {tenant.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <h4 className="font-black text-gray-900 tracking-tight">{tenant.name}</h4>
                  <p className="text-xs text-indigo-600 font-bold uppercase tracking-tighter">{tenant.position}</p>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400 font-bold uppercase tracking-widest">Residency</span>
                  <span className="text-gray-900 font-black">{prop ? prop.name : 'Vacant'}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400 font-bold uppercase tracking-widest">Employer</span>
                  <span className="text-gray-900 font-black">{tenant.employer}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-50">
                {tenant.tags.map((tag, i) => (
                  <span key={i} className="px-2 py-1 bg-gray-50 text-gray-500 text-[10px] font-black rounded-lg uppercase tracking-tight group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <TenantProfileModal
        isOpen={!!selectedTenant}
        onClose={() => setSelectedTenant(null)}
        tenant={selectedTenant}
        documents={data.documents}
        properties={data.properties}
      />

      <TenantFormModal
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); setEditingTenant(null); }}
        onSave={handleSaveTenant}
        tenant={editingTenant}
        customFieldDefs={data.customFieldDefs || []}
      />
    </div>
  );
};

export default Tenants;